// @ts-nocheck
import { CSSProperties, ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  Button,
  NextReviewField,
  Stepper,
  BackButton
} from '@lmc/ui-kit'
import { Col } from 'antd'
import { colors } from '@lmc/common/constants'
import {
  ConsentFormTemplate,
  CreateConsentFormsJSON,
  ConsentType,
  ConsentStatus
} from '@lmc/common/types'
import { Resident } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { Form } from 'antd'
import {
  getTimeOptions,
  onSubmit,
  setNextReviewDateByOptions
} from '../../../utils'
import { v4 as uuidv4 } from 'uuid'
import CloseModal from './components/CloseModal'
import { customConsentFormsEmpty, isMatchingFormBody } from './utils'
import ModalConsentForms from './components/ModalConsentForms'
import ModalSideControlBar from './components/ModalSideControlBar'
import SignLaterModal from './components/SignLaterModal'
import SignNowModal from './components/SignNowModal'
import { isBrowser, isTablet } from 'react-device-detect'
import { CarePlanAdlExtended } from 'components/generic/CarePlanSelector/CarePlanSelector'
import ConsentConfirmationCard, {
  getIsSignStatus,
  getIsConsentConfirmDisabled,
  getNextReviewFieldState
} from '../ConsentConfirmationCard'
import styled from 'styled-components'

type ConsentFormModalProps = {
  fetchResidentConsentForms: (resident: string) => void
  isModalConsentVisible: boolean
  item: string
  category: string
  resident: Resident
  setIsModalConsentVisible: (e: boolean) => void
  templates: ConsentFormTemplate[]
  sendConsentForms: (forms: CreateConsentFormsJSON) => Promise<any>
  closeMainModal: () => void
  consentType: ConsentType
  testId?: string
}

type CustomFormTemplate = {
  id: string
  title: string
  body: string
  editedBy: null
  editedOn: null
  createdBy: null
  createdOn: null
  isCreated: boolean
}

const customFormTemplate = (uuid: () => string): CustomFormTemplate => ({
  id: uuid?.(),
  title: 'Custom Title',
  body: '<p>Custom Form</p>',
  editedBy: null,
  editedOn: null,
  createdBy: null,
  createdOn: null,
  isCreated: false
})

type SideControlItem = {
  id: string
  title: string
  isSelected: boolean
}

const isDesktop = isBrowser || isTablet

const ConsentFormModal = ({
  closeMainModal,
  fetchResidentConsentForms,
  isModalConsentVisible,
  resident,
  sendConsentForms,
  setIsModalConsentVisible,
  templates,
  item,
  category,
  consentType,
  testId
}: ConsentFormModalProps): ReactElement => {
  const { t } = useTranslation()

  const translate = (key: string, variable?: any) =>
    t(`consent.createConsentForms.components.consentModal.${key}`, variable)

  const [sideControlIds, setSideControlIds] = useState<SideControlItem[]>([])

  const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false)

  const [activeStep, setActiveStep] = useState<number>(0)

  const [warning, setWarning] = useState<boolean>(false)

  const [timeOptionNextReview, setTimeOptionNextReview] = useState<
    string | undefined
  >()

  const [nextReviewDate, setNextReviewDate] = useState<string | undefined>()

  const [showSignLaterModal, setShowSignLaterModal] = useState<boolean>(false)

  const [customForms, setCustomForms] = useState<CustomFormTemplate[]>([])

  const [copyOfTemplates, setCopyOfTemplates] = useState<ConsentFormTemplate[]>(
    []
  )

  const [selectedCarePlans, setSelectedCarePlans] = useState<
    Record<string, Array<CarePlanAdlExtended>>
  >({})

  const [consentStatus, setConsentStatus] = useState<{
    status: ConsentStatus
    reason?: string
    lastReviewDate?: string | null
  }>({
    status: ConsentStatus.noStatus
  })

  const nextReviewFieldState = getNextReviewFieldState(consentStatus.status)
  const isNoStatus = consentStatus.status === ConsentStatus.noStatus

  useEffect(() => {
    setCopyOfTemplates(templates)
  }, [templates])

  useEffect(() => {
    setCustomForms([customFormTemplate(uuidv4)])
  }, [])

  const finalTemplates = copyOfTemplates?.filter((item) =>
    sideControlIds?.map((item) => item.isSelected && item.id).includes(item.id)
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [form] = Form.useForm()

  let editableTemplates: string[] = []

  const templatesToSend = finalTemplates?.map((item) => {
    const baseFields = {
      consentTemplate: item.id,
      title: item.title,
      isRefused: false,
      type: consentType,
      carePlans: selectedCarePlans[item.id]?.map((cp) => cp.id) || [],
      consentStatus: consentStatus.status,
      statusReason: consentStatus.reason,
      lastReviewed: consentStatus.lastReviewDate
    }

    if (
      !editableTemplates.includes(item.id) &&
      !isMatchingFormBody(item, templates)
    ) {
      return {
        ...baseFields,
        isCustom: true,
        body: item.body
      }
    }
    return baseFields
  })

  const customFormsToSend = customForms
    ?.map((item) => {
      if (item.isCreated) {
        return {
          isCustom: true,
          body: item.body,
          title: item.title,
          isRefused: false,
          type: consentType,
          carePlans: selectedCarePlans[item.id]?.map((cp) => cp.id) || [],
          consentStatus: consentStatus.status,
          statusReason: consentStatus.reason,
          lastReviewed: consentStatus.lastReviewDate
        }
      }
      return null
    })
    .filter((item) => item !== null)

  const warningNeeded =
    !customForms.some((item) => item.isCreated === true) &&
    sideControlIds?.every((item) => item.isSelected === false)

  // Only require next review date when it's required (not optional or disabled)
  const isNextReviewDateInvalid =
    nextReviewFieldState.isRequired && !nextReviewDate

  const disabledFinishForm =
    isNextReviewDateInvalid ||
    warningNeeded ||
    customConsentFormsEmpty(customFormsToSend, sideControlIds)

  const isConsentConfirmDisabled = getIsConsentConfirmDisabled({
    status: consentStatus.status,
    reason: consentStatus.reason,
    lastReviewDate: consentStatus.lastReviewDate,
    disabled: disabledFinishForm
  })

  /**
   * Synchronizes the sidebar consent controls (sideControlIds) with the current list of consent templates
   * whenever the consent modal is opened.
   *
   * - Builds a fresh array of { id, title, isSelected: false } for every template in copyOfTemplates
   * - Checks if the new list differs from the current sideControlIds (by length or by individual IDs)
   * - Only updates state if something has actually changed
   *
   * This ensures the sidebar always shows an up-to-date checkbox/toggle for each available consent template,
   * with correct titles and a clean unselected state when the modal opens.
   */
  useEffect(() => {
    const newSideControlIds = (copyOfTemplates || []).map((item) => ({
      isSelected: false,
      title: item.title,
      id: item.id
    }))
    const needToUpdateSideControlIds =
      !newSideControlIds.every(
        (sideControll) =>
          !!sideControlIds.find((existing) => existing.id === sideControll.id)
      ) || sideControlIds.length !== newSideControlIds.length

    if (isModalConsentVisible && needToUpdateSideControlIds) {
      setSideControlIds(newSideControlIds)
    }
  }, [isModalConsentVisible, copyOfTemplates])

  useEffect(() => {
    setNextReviewDateByOptions(timeOptionNextReview, setNextReviewDate, form)
  }, [timeOptionNextReview])

  // Clear next review date when status changes to disabled
  useEffect(() => {
    if (nextReviewFieldState.isDisabled) {
      setNextReviewDate(undefined)
      setTimeOptionNextReview(undefined)
      form.setFieldValue('nextReviewDate', undefined)
    }
  }, [nextReviewFieldState.isDisabled, form])

  const handleSelected = (id: string) => {
    const updateArray = sideControlIds.map((item) => {
      if (item.id === id) {
        item.isSelected = !item.isSelected
        return item
      }
      return item
    })
    setSideControlIds(updateArray)
  }

  const handleConfirm = async () => {
    try {
      await sendConsentForms({
        resident: resident.id,
        nextReviewDate: nextReviewDate
          ? dayjs(nextReviewDate, 'DD-MM-YYYY').toISOString()
          : null,
        consents: [...templatesToSend, ...customFormsToSend]
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      // expected output: ReferenceError: nonExistentFunction is not defined
      // Note - error messages will vary depending on browser
    }
    setShowSignLaterModal(true)
  }

  const handleConsentSubmit = async () => {
    const isSignStatus = getIsSignStatus(consentStatus.status)
    if (isSignStatus) {
      setShowSignatureModal(true)
    } else {
      await handleConfirm()
    }
  }

  const handleCloseModals = () => {
    const cleanCustomConsentForms = customConsentFormsEmpty(
      customFormsToSend,
      sideControlIds
    )
    if (cleanCustomConsentForms || !warningNeeded) {
      setWarning(true)
    }
    if (warning || warningNeeded || !cleanCustomConsentForms) {
      finalCloseModal()
    }
  }

  const finalCloseModal = () => {
    setShowSignLaterModal(false)
    fetchResidentConsentForms(resident?.id)
    closeMainModal()
    setCustomForms([customFormTemplate(uuidv4)])
    setWarning(false)
    setSideControlIds([])
  }

  const onSubmitSign = async ({ formState }) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    const res = await onSubmit({
      resident,
      nextReviewDate,
      consents: [...templatesToSend, ...customFormsToSend],
      formState,
      item,
      category
    })
    if (res) {
      setShowSignatureModal(false)
      finalCloseModal()
    }
  }

  const handleDateChange = (date) => {
    if (date !== null) {
      setNextReviewDate(date)
    } else {
      setTimeOptionNextReview(undefined)
      setNextReviewDate(undefined)
    }
  }

  const handleEditForm = (id: string) => {
    editableTemplates.push(id)
  }

  const handleCancelEditForm = (id: string) => {
    const filteredTemplates = editableTemplates.filter((item) => item !== id)
    editableTemplates = filteredTemplates
  }

  const handleCustomEntry = () => {
    setCustomForms((oldForms) => [
      ...oldForms.map((item, index) => {
        if (index === customForms.length - 1) {
          return {
            ...item,
            isCreated: true
          }
        }
        return item
      }),
      customFormTemplate(uuidv4)
    ])
  }

  const backButton = (
    <BackButton
      onClick={() => handleCloseModals()}
      data-testid={
        testId
          ? `${testId}-consent-form-back-button`
          : 'consent-form-back-button'
      }
    />
  )

  const firstCol = (
    <div
      data-testid={
        testId
          ? `${testId}-consent-form-first-column`
          : 'consent-form-first-column'
      }
    >
      {isDesktop && backButton}
      <ModalSideControlBar
        isMobile={!isDesktop}
        handleSelected={handleSelected}
        sideControlIds={sideControlIds}
        translate={translate}
        customEntry={handleCustomEntry}
        consentType={consentType}
        testId={testId}
      />
      {!isDesktop && (
        <Button
          variant="default"
          style={styles.nextButton as CSSProperties}
          data-testid={
            testId
              ? `${testId}-consent-form-next-button`
              : 'consent-form-next-button'
          }
        >
          {t('general.buttons.next')}
        </Button>
      )}
    </div>
  )

  const secondCol = (
    <div
      data-testid={
        testId
          ? `${testId}-consent-form-second-column`
          : 'consent-form-second-column'
      }
    >
      <Form
        form={form}
        data-testid={
          testId ? `${testId}-consent-form-form` : 'consent-form-form'
        }
      >
        <ConsentConfirmationCard
          disabled={isConsentConfirmDisabled}
          onSubmit={isNoStatus ? undefined : handleConsentSubmit}
          onStatusChange={setConsentStatus}
          selectedStatus={consentStatus}
        />
        {isNoStatus && <Spacer />}
        {!isNoStatus && (
          <>
            <Spacer />
            <NextReviewField
              disabled={nextReviewFieldState.isDisabled}
              isMobile
              localisation={{
                title: nextReviewFieldState.isRequired ? (
                  <>
                    {translate('nextReview')} <RedAsterisk>*</RedAsterisk>
                  </>
                ) : (
                  translate('nextReview')
                )
              }}
              dateValue={nextReviewDate}
              datePickerOnChange={handleDateChange}
              radioGroupOnChange={setTimeOptionNextReview}
              timeValue={timeOptionNextReview}
              timeOptions={getTimeOptions(translate)}
              data-testid={
                testId
                  ? `${testId}-consent-form-next-review-field`
                  : 'consent-form-next-review-field'
              }
            />
          </>
        )}
        <ModalConsentForms
          copyOfTemplates={copyOfTemplates}
          handleCancelEditForm={handleCancelEditForm}
          handleEditForm={handleEditForm}
          resident={resident}
          setCopyOfTemplates={setCopyOfTemplates}
          sideControlIds={sideControlIds}
          templates={templates}
          translate={translate}
          customForms={customForms}
          setCustomForms={setCustomForms}
          testId={testId}
          onCarePlanChange={setSelectedCarePlans}
        />
      </Form>
    </div>
  )

  const steps = [firstCol, secondCol]

  return (
    <>
      <Modal
        greyBackground
        isMobile={!isDesktop}
        fullScreen
        onCancel={() => setIsModalConsentVisible(false)}
        setIsModalVisible={setIsModalConsentVisible}
        isModalVisible={isModalConsentVisible}
        testId={testId}
      >
        {!isDesktop ? (
          <>
            <div
              style={styles.backButtonContainer as CSSProperties}
              data-testid={
                testId
                  ? `${testId}-consent-form-mobile-back-container`
                  : 'consent-form-mobile-back-container'
              }
            >
              {backButton}
            </div>
            <div
              style={styles.stepperContainer}
              data-testid={
                testId
                  ? `${testId}-consent-form-stepper-container`
                  : 'consent-form-stepper-container'
              }
            >
              <Stepper
                allActiveSteps
                onStepClick={setActiveStep}
                activeStep={activeStep}
                steps={[
                  {
                    title: translate('createConsent'),
                    content: undefined,
                    route: ''
                  },
                  {
                    title: translate('viewConsent'),
                    content: undefined,
                    route: undefined
                  }
                ]}
                data-testid={
                  testId
                    ? `${testId}-consent-form-stepper`
                    : 'consent-form-stepper'
                }
              />
            </div>
            <div
              style={
                !isDesktop && activeStep !== 0 ? styles.columnsContainer : {}
              }
              data-testid={
                testId
                  ? `${testId}-consent-form-mobile-content`
                  : 'consent-form-mobile-content'
              }
            >
              {steps[activeStep]}
            </div>
          </>
        ) : (
          <div
            data-testid={
              testId
                ? `${testId}-consent-form-desktop-layout`
                : 'consent-form-desktop-layout'
            }
          >
            <Col style={styles.firstCol}>{firstCol}</Col>
            <Col style={styles.secondCol}>{secondCol}</Col>
          </div>
        )}
      </Modal>
      <SignLaterModal
        handleCloseModals={handleCloseModals}
        setShowSignLaterModal={setShowSignLaterModal}
        showSignLaterModal={showSignLaterModal}
        translate={translate}
        testId={testId}
      />
      <SignNowModal
        onSubmitSign={onSubmitSign}
        resident={resident}
        setShowSignatureModal={setShowSignatureModal}
        showSignatureModal={showSignatureModal}
        translate={translate}
        testId={testId}
      />
      <CloseModal
        showCloseModal={warning}
        setShowCloseModal={setWarning}
        onConfirm={handleCloseModals}
        onCancel={() => setWarning(false)}
        testId={testId}
      />
    </>
  )
}

export const styles = {
  firstCol: {
    marginRight: 100,
    minWidth: 300
  },
  secondCol: {
    marginLeft: 320,
    marginTop: 100,
    marginRight: 50,
    paddingBottom: 50
  },
  title: {
    textAlign: 'center',
    fontSize: 17,
    fontWeight: 700
  },
  subTitle: { textAlign: 'center', fontSize: 13, fontWeight: 300 },
  subTitleStrong: {
    fontWeight: 600,
    color: '#E6699C',
    opacity: 1,
    cursor: 'pointer'
  },
  labelTextBox: { marginTop: 50 },
  listSectionHeader: { marginTop: 50, marginBottom: 50 },
  redTitle: { fontWeight: 700, fontSize: 13, color: colors.red },
  submitButton: {
    marginRight: 4,
    justifyContent: 'end',
    display: 'flex'
  },
  signLaterModal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: 100,
    marginBottom: 80
  },
  linkButton: {
    color: colors.pink,
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  laterTitle: {
    fontWeight: 400,
    color: colors.grey,
    fontSize: 18
  },
  laterSubTitle: {
    fontWeight: 400,
    fontSize: 14,
    marginBottom: 10,
    marginTop: 10
  },
  backButton: {
    marginLeft: 25
  },
  stepperContainer: {
    margin: 50
  },
  backButtonContainer: {
    margin: 30,
    marginLeft: 0,
    marginTop: 0
  },
  columnsContainer: {
    marginLeft: 30,
    marginRight: 30
  },
  nextButton: {
    float: 'right'
  }
}

const Spacer = styled.div`
  margin-bottom: 16px;
`

const RedAsterisk = styled.span`
  color: ${colors.red};
`

export default ConsentFormModal
